import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useAuth } from '../../store/auth';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { useToast } from '../../components/ui/Toast';
import { ErrorNotice, PageHeader } from '../../components/ui';
import { Field } from '../../components/ui/Form';

/**
 * Your own account.
 *
 * Only one thing here changes: your password. Everything else about who you are
 * — your name, your role, which branches you work — is set by whoever manages
 * staff, because letting somebody edit their own role is letting them promote
 * themselves.
 */
export function AccountPage() {
  const session = useAuth((s) => s.session);
  const signOut = useAuth((s) => s.signOut);
  const onEnter = useKeyboardForm();
  const toast = useToast();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<unknown>(null);

  const change = useMutation({
    mutationFn: () => api.post('/auth/change-password', { currentPassword: current, newPassword: next }),
    onSuccess: () => {
      toast('Password changed', 'Sign in again with the new one');
      // Changing the password ends every session, this one included, so the
      // honest thing is to send them back to sign in.
      setTimeout(signOut, 1500);
    },
    onError: setError,
  });

  const mismatch = confirm !== '' && next !== confirm;
  const tooShort = next !== '' && next.length < 8;
  const ready = current !== '' && next.length >= 8 && next === confirm;

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (ready) change.mutate();
  };

  return (
    <>
      <PageHeader title="My account" subtitle={session?.user.fullName} />

      <div className="max-w-md space-y-6">
        <section className="card">
          <h2 className="mb-1 text-sm font-semibold">Who you are</h2>
          <dl className="text-sm">
            <div className="flex justify-between py-1">
              <dt className="text-ink-soft">Name</dt>
              <dd>{session?.user.fullName}</dd>
            </div>
            <div className="flex justify-between py-1">
              <dt className="text-ink-soft">Username</dt>
              <dd className="num">{session?.user.username}</dd>
            </div>
            <div className="flex justify-between py-1">
              <dt className="text-ink-soft">Role</dt>
              <dd>{session?.roles[0]?.replace(/_/g, ' ').toLowerCase()}</dd>
            </div>
          </dl>
          <p className="mt-2 text-2xs text-ink-faint">
            To change any of this, ask the shop owner — it is theirs to set.
          </p>
        </section>

        <section className="card">
          <h2 className="mb-3 text-sm font-semibold">Change your password</h2>
          <ErrorNotice error={error} />

          <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="space-y-3">
            <Field label="Current password" required>
              <input
                type="password"
                className="field"
                value={current}
                autoComplete="current-password"
                onChange={(e) => setCurrent(e.target.value)}
              />
            </Field>

            <Field
              label="New password"
              required
              hint="At least eight characters."
              error={tooShort ? 'Too short — eight characters or more.' : null}
            >
              <input
                type="password"
                className="field"
                value={next}
                autoComplete="new-password"
                onChange={(e) => setNext(e.target.value)}
              />
            </Field>

            <Field label="New password again" required error={mismatch ? 'These do not match.' : null}>
              <input
                type="password"
                className="field"
                value={confirm}
                autoComplete="new-password"
                onChange={(e) => setConfirm(e.target.value)}
              />
            </Field>

            <button className="btn-primary" disabled={!ready || change.isPending}>
              {change.isPending ? 'Changing…' : 'Change password'}
            </button>
            <p className="text-2xs text-ink-faint">
              Changing your password signs you out everywhere, so you will sign in again with the new one.
            </p>
          </form>
        </section>
      </div>
    </>
  );
}
